import { Component } from "react";
import { AlertTriangle, RefreshCw } from "lucide-react";

/**
 * Generic error boundary used to wrap the estimate render area so a malformed
 * AI response can never blank-screen the whole app. Shows a recoverable
 * fallback with a "Try again" action.
 */
export default class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    // Surface to console for debugging in dev. In prod, hook to a logger.
    console.error("[ErrorBoundary]", error, info?.componentStack);
  }

  reset = () => this.setState({ error: null });

  render() {
    if (this.state.error) {
      return (
        <div
          className="border border-red-500/40 bg-red-500/5 p-8 text-center"
          data-testid="error-boundary-fallback"
        >
          <AlertTriangle className="h-10 w-10 text-red-400 mx-auto" />
          <h3 className="mt-4 text-lg font-bold text-white">
            We couldn't render this section
          </h3>
          <p className="mt-2 text-sm text-slate-400 max-w-md mx-auto">
            The AI returned an unexpected response shape. Your data is safe — try
            regenerating the estimate, or refresh the page.
          </p>
          <p className="mt-4 text-xs text-red-300/70 font-mono break-all">
            {String(this.state.error?.message || this.state.error)}
          </p>
          <button
            onClick={this.reset}
            className="mt-6 inline-flex items-center gap-2 px-4 py-2 bg-[#FF6A00] hover:bg-[#E65C00] text-white text-sm rounded-md transition-colors"
            data-testid="error-boundary-reset-btn"
          >
            <RefreshCw className="h-4 w-4" /> Try again
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}
